import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react'
import authService from '../../services/authService'
import Logo from '../../components/Logo'

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type ForgotPasswordForm = z.infer<typeof schema>

export default function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isSent, setIsSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: ForgotPasswordForm) => {
    setIsLoading(true)
    try {
      await authService.forgotPassword(data.email)
      setIsSent(true)
    } catch {
      // Always show success to avoid revealing if email exists
      setIsSent(true)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-8 bg-secondary-50">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo to="/" height={40} />
        </div>

        {isSent ? (
          <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">Check Your Email</h1>
            <p className="text-secondary-500 mb-6">
              If an account exists with that email, we've sent a password reset link.
              Please check your inbox and spam folder.
            </p>
            <Link to="/login" className="btn btn-primary w-full">
              Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-secondary-900">Forgot Password?</h1>
              <p className="text-secondary-500 mt-2">
                Enter your email and we'll send you a reset link
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-card border border-secondary-200 p-8">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
                    <input
                      id="email"
                      type="email"
                      className={`input pl-12 ${errors.email ? 'border-red-500' : ''}`}
                      placeholder="you@example.com"
                      {...register('email')}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1.5">{errors.email.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn btn-accent w-full py-3 text-base font-semibold flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-secondary-100">
                <Link
                  to="/login"
                  className="flex items-center justify-center gap-2 text-sm text-secondary-600 hover:text-secondary-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Sign In
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
